import * as React from "react"
import { useMemo, useRef, useCallback } from "react"
import { useMotionValue, useDragControls, DraggableProps, PanInfo, BoundingBox2D, MotionValue } from "framer-motion"
import sync from "framesync"
import { ControlType } from "../../render/types/PropertyControls"
import { FrameWithMotion } from "../../render/presentation/Frame/FrameWithMotion"
import { EmptyState } from "../../components/EmptyState"
import { addPropertyControls } from "../../utils/addPropertyControls"
import { isMotionValue } from "../../render/utils/isMotionValue"
import { RenderTarget } from "../../render/types/RenderEnvironment"
import { isFiniteNumber } from "../../render/utils/isFiniteNumber"
import { ScrollProps } from "./types"
import { useWheelScroll } from "./useWheelScroll"
import { useLayoutId } from "../../render/utils/useLayoutId"
import { Rect, useIsInCurrentNavigationTarget } from "../../index"
export type { ScrollProps, ScrollEvents, ScrollConfig } from "./types"

const directionMap: { [key: string]: DraggableProps["drag"] } = {
    horizontal: "x",
    vertical: "y",
    both: true,
}

function convertScrollDirectionToDrag(scrollDirection?: "horizontal" | "vertical" | "both") {
    return scrollDirection ? directionMap[scrollDirection] : scrollDirection
}

const useUpdateChildSize = (
    dragDirection: "both" | "horizontal" | "vertical",
    children: React.ReactNode
): React.ReactNode | React.ReactNode[] => {
    return useMemo((): React.ReactNode | React.ReactChild[] => {
        return React.Children.map(children, (child: React.ReactChild) => {
            if (child === null || typeof child !== "object" || typeof child.type === "string") {
                return child
            }

            const update: Partial<{ width: number | string; height: number | string }> = {}
            switch (dragDirection) {
                case "vertical":
                    update.width = "100%"
                    break
                case "horizontal":
                    update.height = "100%"
                    break
                default:
                    return child
            }

            return React.cloneElement(child, update)
        })
    }, [dragDirection, children])
}

const numberFromOptionalMotionValue = (value: number | MotionValue<number>): number => {
    return typeof value === "number" ? value : value.get()
}
/**
 * @public
 */
export function Scroll(props: ScrollProps) {
    const {
        direction = "vertical",
        directionLock = false,
        dragEnabled: dragEnabled = true,
        dragElastic,
        dragMomentum,
        dragTransition,
        wheelEnabled = true,
        contentOffsetX = 0,
        contentOffsetY = 0,
        contentWidth,
        contentHeight,
        onScrollStart,
        onScroll,
        onScrollEnd,
        onDragStart,
        onDrag,
        onDragEnd,
        onUpdate,
        onDirectionLock,
        style,
        children,
        scrollAnimate,
        resetOffset,
        __layoutId,
        ...containerProps
    } = props
    const layoutId = useLayoutId(props, __layoutId, "scroll")
    const defaultX = useMotionValue(typeof contentOffsetX === "number" ? contentOffsetX : 0)
    const defaultY = useMotionValue(typeof contentOffsetY === "number" ? contentOffsetY : 0)
    const x = isMotionValue(contentOffsetX) ? contentOffsetX : defaultX
    const y = isMotionValue(contentOffsetY) ? contentOffsetY : defaultY
    const measuredConstraints = useRef<null | BoundingBox2D>(null)
    const dragControls = useDragControls()
    const isInTarget = useIsInCurrentNavigationTarget()
    const wasInTargetRef = useRef(true)

    React.useEffect(() => {
        dragControls.updateConstraints()
    })

    function setMeasureDragConstraints(constraints: BoundingBox2D) {
        constraints = offsetToZero(constraints)

        if (contentWidth !== undefined) constraints.left = -contentWidth
        if (contentHeight !== undefined) constraints.top = -contentHeight

        return (measuredConstraints.current = constraints)
    }

    const { initial, prev } = useRef({
        initial: { x: 0, y: 0 },
        prev: { x: 0, y: 0 },
    }).current

    const isPreview = RenderTarget.current() === RenderTarget.preview

    const containerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const lastOffsetRef = useRef<{
        maxXOffset: number
        maxYOffset: number
        offsetX: number
        offsetY: number
        x: number
        y: number
    } | null>(null)

    function shouldResetScroll(inTarget: boolean) {
        const hasEnteredTarget = inTarget && wasInTargetRef.current === false
        return resetOffset && hasEnteredTarget
    }

    function measureAndUpdateScrollOffset(): void {
        if (!contentRef.current || !containerRef.current) return

        // If the component is flagged to reset its scroll offset to its initial
        // values when it appears, check if the scroll was previously not
        // visible, and has become visible, and if so flag that we must reset
        // the scroll offset.
        const mustReset = shouldResetScroll(isInTarget)
        wasInTargetRef.current = isInTarget

        // If the offset properties are not set, we don't need to measure and
        // update scroll offset.
        const previous = lastOffsetRef.current
        if (previous === null && contentOffsetX === undefined && contentOffsetY === undefined) return

        const currentOffsetX = direction !== "vertical" ? numberFromOptionalMotionValue(contentOffsetX) : 0
        const currentOffsetY = direction !== "horizontal" ? numberFromOptionalMotionValue(contentOffsetY) : 0

        // Check if the values of the offset props have changed, requiring
        // scroll position to be updated.
        const shouldUpdateOffset =
            previous === null || currentOffsetX !== previous.offsetX || currentOffsetY !== previous.offsetY

        // Check if the new maximum scroll offset has changed as a result of the
        // container or the scrollable content resizing.
        const currentMaxXOffset = contentRef.current.offsetWidth - containerRef.current.offsetWidth
        const currentMaxYOffset = contentRef.current.offsetHeight - containerRef.current.offsetHeight
        const hasSizeChanged = currentMaxXOffset !== previous?.maxXOffset || currentMaxYOffset !== previous?.maxYOffset

        // Check if the actual scroll offset has changed via user interaction
        // relative to the last offset set via this function.
        const hasScrollOffsetChanged = previous?.x !== x.get() || previous?.y !== y.get()

        // If the size has changed, but the user hasn't scrolled away from the
        // last set scroll offset, we expect the resize not to result in an
        // incorrect scroll position. For instance, the maximum scrollable size
        // could shrink, meaning that a scroll offset that was previously
        // acceptable now results in undesirable overscroll. In this case we
        // need to reset to an acceptable offset.
        const shouldStayPinned = hasSizeChanged && !hasScrollOffsetChanged

        if (mustReset || shouldUpdateOffset || shouldStayPinned) {
            const nextXOffset = -Math.min(currentOffsetX, currentMaxXOffset)
            const nextYOffset = -Math.min(currentOffsetY, currentMaxYOffset)

            x.set(nextXOffset)
            y.set(nextYOffset)

            lastOffsetRef.current = {
                maxXOffset: currentMaxXOffset,
                maxYOffset: currentMaxYOffset,
                offsetX: currentOffsetX,
                offsetY: currentOffsetY,
                x: nextXOffset,
                y: nextYOffset,
            }
        }
    }

    // onBeforeLayoutMeasure is not called on the canvas, so handle updating
    // scroll offset on the canvas on any update with a layout effect.
    React.useLayoutEffect(() => {
        if (RenderTarget.current() !== RenderTarget.canvas) return

        measureAndUpdateScrollOffset()
    })

    // onBeforeLayoutMeasure is not called on mount, so handle updating scroll
    // on mount in the preview.
    React.useLayoutEffect(() => {
        if (RenderTarget.current() === RenderTarget.canvas) return

        measureAndUpdateScrollOffset()
    }, [])

    // isInTarget can change without motion calling measure on the element. To
    // solve that case, we run an effect when isInTarget changes, and call
    // measureAndUpdate if we need to reset. Since an effect runs after motion's
    // measurements phase, this will only run if we haven't already reset during
    // the render phase.
    React.useEffect(() => {
        if (shouldResetScroll(isInTarget)) measureAndUpdateScrollOffset()

        // Always unset isInTarget to false on a change.
        // measureAndUpdateScrollOffset may not be called when navigating off
        // screen.
        if (isInTarget === false) wasInTargetRef.current = false
    }, [isInTarget])

    const getLatestPoint = () => ({ x: x.get(), y: y.get() })

    const resetInitialPoint = useCallback(() => {
        const point = getLatestPoint()
        initial.x = point.x
        initial.y = point.y
        prev.x = point.x
        prev.y = point.y
    }, [])

    const getPointData = useCallback(() => {
        const point = getLatestPoint()

        const data = {
            point,
            velocity: { x: x.getVelocity(), y: y.getVelocity() },
            offset: { x: point.x - initial.x, y: point.y - initial.y },
            delta: { x: point.x - prev.x, y: point.y - prev.y },
        }

        prev.x = point.x
        prev.y = point.y

        return data
    }, [x, y])

    const updateScrollListeners = useCallback(() => {
        onUpdate && onUpdate({ x: x.get(), y: y.get() })
        onScroll && onScroll(getPointData())
    }, [onScroll, onUpdate, getPointData, x, y])

    const scheduleUpdateScrollListeners = useCallback(() => {
        sync.update(updateScrollListeners, false, true)
    }, [updateScrollListeners])

    const onMotionDragStart = (event: MouseEvent | TouchEvent, info: PanInfo) => {
        resetInitialPoint()
        onDragStart && onDragStart(event, info)
        onScrollStart && onScrollStart(info)
    }

    const onMotionDragTransitionEnd = () => onScrollEnd && onScrollEnd(getPointData())

    useWheelScroll(containerRef, {
        enabled: wheelEnabled,
        initial,
        prev,
        direction,
        offsetX: x,
        offsetY: y,
        onScrollStart,
        onScroll,
        onScrollEnd,
        constraints: measuredConstraints,
    })

    const overdragX = useMotionValue(0)
    const overdragY = useMotionValue(0)

    React.useLayoutEffect(() => {
        const setScrollX = (xValue: number) => {
            const element = containerRef.current
            if (!(element instanceof HTMLDivElement)) return
            element.scrollLeft = -xValue

            const constraints = measuredConstraints.current
            if (constraints) {
                let overdragXValue = 0
                if (xValue > constraints.right) overdragXValue = xValue
                if (xValue < constraints.left) overdragXValue = xValue - constraints.left
                overdragX.set(overdragXValue)
            }

            scheduleUpdateScrollListeners()
        }

        const currentX = x.get()
        if (currentX !== 0) setScrollX(currentX)

        return x.onChange(setScrollX)
    }, [x, overdragX, scheduleUpdateScrollListeners])

    React.useLayoutEffect(() => {
        const setScrollY = (yValue: number) => {
            const element = containerRef.current
            if (!(element instanceof HTMLDivElement)) return

            element.scrollTop = -yValue

            const constraints = measuredConstraints.current
            if (constraints) {
                let overdragYValue = 0

                if (yValue > constraints.bottom) overdragYValue = yValue
                if (yValue < constraints.top) overdragYValue = yValue - constraints.top
                overdragY.set(overdragYValue)
            }

            scheduleUpdateScrollListeners()
        }

        const currentY = y.get()
        if (currentY !== 0) setScrollY(currentY)

        return y.onChange(setScrollY)
    }, [y, overdragY, scheduleUpdateScrollListeners])

    const nativeOnScroll = React.useCallback(() => {
        const element = containerRef.current
        if (!(element instanceof HTMLDivElement)) return
        // we ignore native scroll changes when we are dragging or finishing a drag animation
        const xDelta = Math.abs(x.get() + element.scrollLeft)
        const yDelta = Math.abs(y.get() + element.scrollTop)
        if (xDelta > 1) x.set(-element.scrollLeft)
        if (yDelta > 1) y.set(-element.scrollTop)
    }, [x, y])

    const isEmpty = React.Children.count(children) === 0
    const width = direction !== "vertical" && !isEmpty ? "auto" : "100%"
    const height = direction !== "horizontal" && !isEmpty ? "auto" : "100%"
    const size = !containerProps.__fromCanvasComponent
        ? {
              width: containerProps.__fromCodeComponentNode ? "100%" : containerProps.width,
              height: containerProps.__fromCodeComponentNode ? "100%" : containerProps.height,
          }
        : {}

    return (
        <FrameWithMotion
            data-framer-component-type="Scroll"
            background="none" // need to set here to prevent default background from Frame
            {...containerProps}
            {...size}
            style={{
                ...style,
                willChange: isPreview ? "transform" : undefined, // allows the scroll content to be hardware accelerated
                overflow: "hidden",
            }}
            onScroll={nativeOnScroll}
            preserve3d={containerProps.preserve3d}
            ref={containerRef}
            layoutId={layoutId}
            onBeforeLayoutMeasure={measureAndUpdateScrollOffset}
        >
            <FrameWithMotion
                data-framer-component-type="ScrollContentWrapper"
                animate={scrollAnimate}
                drag={dragEnabled && convertScrollDirectionToDrag(direction)}
                dragDirectionLock={directionLock}
                dragElastic={dragElastic}
                dragMomentum={dragMomentum}
                dragTransition={dragTransition}
                dragConstraints={containerRef}
                dragControls={dragControls}
                onDragStart={onMotionDragStart}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
                onDragTransitionEnd={onMotionDragTransitionEnd}
                onDirectionLock={onDirectionLock}
                onMeasureDragConstraints={setMeasureDragConstraints}
                width={width}
                height={height}
                _dragX={x}
                _dragY={y}
                position="relative"
                x={overdragX}
                y={overdragY}
                ref={contentRef}
                style={{
                    display: isEmpty ? "block" : "inline-block",
                    willChange: isPreview ? "transform" : undefined, // makes the scroll content hardware accelerated
                    backgroundColor: "transparent",
                    overflow: "visible",
                    minWidth: "100%",
                    minHeight: "100%",
                }}
                preserve3d={containerProps.preserve3d}
            >
                <EmptyState
                    children={children!}
                    size={{
                        width: isFiniteNumber(containerProps.width) ? containerProps.width : "100%",
                        height: isFiniteNumber(containerProps.height) ? containerProps.height : "100%",
                    }}
                    insideUserCodeComponent={!containerProps.__fromCodeComponentNode}
                    title="Scroll"
                    description="Click and drag the connector to any frame on the canvas →"
                />
                {useUpdateChildSize(direction, children)}
            </FrameWithMotion>
        </FrameWithMotion>
    )
}

/**
 * Because we're overriding the usual drag x/y with scrollTop and scrollLeft
 * our constraints calculations need rebasing to 0
 */
function offsetToZero({ top, left, right, bottom }: BoundingBox2D) {
    const width = right - left
    const height = bottom - top

    return {
        top: -height,
        left: -width,
        right: 0,
        bottom: 0,
    }
}

addPropertyControls(Scroll, {
    direction: {
        type: ControlType.SegmentedEnum,
        title: "Direction",
        options: ["vertical", "horizontal", "both"],
        defaultValue: "vertical",
    },
    contentOffsetX: {
        type: ControlType.Number,
        title: "Offset X",
        defaultValue: 0,
        min: 0,
        step: 10,
        displayStepper: true,
        hidden: ({ direction }) => direction === "vertical",
    },
    contentOffsetY: {
        type: ControlType.Number,
        title: "Offset Y",
        defaultValue: 0,
        min: 0,
        step: 10,
        displayStepper: true,
        hidden: ({ direction }) => direction === "horizontal",
    },
    directionLock: {
        type: ControlType.Boolean,
        title: "Lock",
        enabledTitle: "1 Axis",
        disabledTitle: "Off",
        defaultValue: true,
    },
    dragEnabled: {
        type: ControlType.Boolean,
        title: "Drag",
        enabledTitle: "On",
        disabledTitle: "Off",
        defaultValue: true,
    },
    wheelEnabled: {
        type: ControlType.Boolean,
        title: "Wheel",
        enabledTitle: "On",
        disabledTitle: "Off",
        defaultValue: true,
    },
    resetOffset: {
        type: ControlType.Boolean,
        title: "Reset",
        enabledTitle: "True",
        disabledTitle: "False",
        defaultValue: false,
    },
})
;(Scroll as any).supportsConstraints = true
